import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface OutreachTask {
  id: string;
  user_id: string;
  referral_lead_id: string;
  title: string;
  description: string | null;
  due_date: string;
  completed: boolean;
  completed_at: string | null;
  sequence_step: number | null;
  sequence_type: string | null;
  created_at: string;
}

export function useOutreachTasks(referralLeadId?: string) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<OutreachTask[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    if (!user) { setTasks([]); setLoading(false); return; }
    let query = supabase.from("outreach_tasks").select("*").order("due_date", { ascending: true });
    if (referralLeadId) query = query.eq("referral_lead_id", referralLeadId);
    const { data } = await query;
    setTasks((data as any[]) ?? []);
    setLoading(false);
  }, [user, referralLeadId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const createTasks = useCallback(async (taskList: Omit<OutreachTask, "id" | "created_at" | "completed" | "completed_at">[]) => {
    const { error } = await supabase.from("outreach_tasks").insert(taskList as any[]);
    if (error) throw error;
    await fetchTasks();
  }, [fetchTasks]);

  const completeTask = useCallback(async (id: string) => {
    const { error } = await supabase.from("outreach_tasks").update({ completed: true, completed_at: new Date().toISOString() } as any).eq("id", id);
    if (error) throw error;
    await fetchTasks();
  }, [fetchTasks]);

  const uncompleteTask = useCallback(async (id: string) => {
    const { error } = await supabase.from("outreach_tasks").update({ completed: false, completed_at: null } as any).eq("id", id);
    if (error) throw error;
    await fetchTasks();
  }, [fetchTasks]);

  const deleteTasks = useCallback(async (referralLeadId: string) => {
    const { error } = await supabase.from("outreach_tasks").delete().eq("referral_lead_id", referralLeadId);
    if (error) throw error;
    await fetchTasks();
  }, [fetchTasks]);

  return { tasks, loading, createTasks, completeTask, uncompleteTask, deleteTasks, refetch: fetchTasks };
}
